import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TIMEZONES } from "@/config";
import {
  Save,
  Building2,
  Users,
  Mail,
  Link as LinkIcon,
  Image as ImageIcon,
  FileEdit,
  Globe,
  PenTool,
  FileText,
  Search,
  ArrowLeft,
  Calendar,
  Plus,
  Trash2,
  Clock,
  GripVertical,
  ChevronDown,
  Loader2,
  Eye,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { CampaignPreviewModal } from "./CampaignPreviewModal";
import { Editor } from "@tinymce/tinymce-react";
import { saveDraft } from "@/utils/draftHelpers";
import { generateSlug, useDebounce } from "@/utils/helpers";
import slugService from "@/services/slugService";
import { useAllRecruiters } from "@/hooks/queries/recruiters";
import {
  useCreateCampaign,
  useUpdateCampaign,
} from "@/hooks/mutations/campaigns";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import useToastStore from "@/components/Common/Toast/toastStore";

import { useGetCampaign } from "@/hooks/queries/campaigns";
import Dropdown from "../ui/Dropdown";
import PageHeader from "@/components/Common/PageHeader";
import { useCompanyById } from "@/hooks/queries/useCompanies";

// Add reorder helper function
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const validateSlugFormat = (slug) => {
  if (!slug) return true;
  const slugRegex = /^[a-z0-9]+(-[a-z0-9]+)*$/;
  return slugRegex.test(slug);
};

// Define a memoized header card component to prevent unnecessary re-renders
const MemoizedHeaderCard = React.memo(
  ({ header, index, onRemove, onChange, provided, isDragging }) => {
    const [errors, setErrors] = useState({});
    const editorRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const [localContent, setLocalContent] = useState(header.value || "");

    // Sync local content with header value
    useEffect(() => {
      setLocalContent(header.value || "");
    }, [header.value]);

    const validateHeader = (field, value) => {
      // Both 'value' (Text) and 'label' (Title) are optional
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return true;
    };

    const handleChange = (field, value) => {
      validateHeader(field, value);
      onChange(index, field, value);
    };

    const handleEditorBlur = () => {
      setIsEditing(false);
      // Clean content and validate
      const cleanedContent = localContent.replace(/^<p>|<\/p>$/g, "").trim();
      setLocalContent(cleanedContent);
      handleChange("value", cleanedContent);
    };

    const handleEditorFocus = () => {
      setIsEditing(true);
    };

    const handleContentChange = (content) => {
      setLocalContent(content);
      // Clean content before updating
      const cleanedContent = content.replace(/^<p>|<\/p>$/g, "").trim();
      handleChange("value", cleanedContent);
    };

    const { showToast } = useToastStore;
    return (
      <div
        {...provided.dragHandleProps}
        className="bg-card rounded-lg shadow-sm p-6 border border-border hover:border-secondary transition-all duration-200"
        style={{
          ...(isEditing && {
            transform: "scale(1.02)",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
            zIndex: 10,
          }),
        }}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="cursor-grab text-muted-foreground hover:text-foreground">
              <GripVertical className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-medium text-foreground">
              Header Section {index + 1}
            </h3>
          </div>
          {index > 0 && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-500/10 rounded-full transition-colors duration-200"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-foreground text-sm font-medium mb-2">
              Title
            </label>
            <input
              type="text"
              placeholder="Enter header label"
              value={header.label}
              onChange={(e) => handleChange("label", e.target.value)}
              onBlur={(e) => validateHeader("label", e.target.value)}
              className={`w-full p-3 !bg-gray-50 dark:!bg-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5ba88c] text-foreground min-h-[10px] ${
                errors.label ? " border-red-500" : ""
              }`}
            />
            {errors.label && (
              <p className="text-red-500 text-sm mt-1">{errors.label}</p>
            )}
          </div>
          <div>
            <label className="block text-foreground text-sm font-medium mb-2">
              Text
            </label>
            <TinyMCEWrapper
              value={localContent}
              onChange={handleContentChange}
              editorRef={editorRef}
              onFocus={handleEditorFocus}
              onBlur={handleEditorBlur}
              isDragging={isDragging}
              sectionId={`header-${index}`}
              index={index}
            />
            {errors.value && (
              <p className="text-red-500 text-sm mt-1">{errors.value}</p>
            )}
          </div>
        </div>
      </div>
    );
  },
);

// Create a wrapper component for TinyMCE editor to handle its lifecycle
const TinyMCEWrapper = React.memo(
  ({
    value,
    onChange,
    editorRef,
    onFocus,
    onBlur,
    isDragging,
    sectionId,
    index,
  }) => {
    // Use a local state to cache the editor content during drag operations
    const [cachedContent, setCachedContent] = useState(value || "");

    // Update cached content when value prop changes
    useEffect(() => {
      if (!isDragging && value !== cachedContent) {
        setCachedContent(value || "");
      }
    }, [value, isDragging]);

    // Skip rendering the editor when dragging to prevent issues
    if (isDragging) {
      return (
        <div className="bg-muted h-[500px] flex items-center justify-center rounded-lg border-2 border-dashed border-secondary">
          <p className="text-foreground text-center">
            Content editor will appear after dragging
          </p>
        </div>
      );
    }

    const handleImageUpload = async (blobInfo) => {
      try {
        const formData = new FormData();
        formData.append("file", blobInfo.blob(), blobInfo.filename());

        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/campaigns`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!response.ok) throw new Error("Upload failed");

        const data = await response.json();
        return data.location;
      } catch (error) {
        console.error("Image upload failed:", error);
        throw error;
      }
    };

    // Generate a stable ID for this editor instance based on section id or index
    const stableId = `editor-${sectionId || `idx-${index}`}`;

    // Clean content function to remove leading/trailing <p> tags
    const cleanContent = (content) => {
      return content.replace(/^<p>|<\/p>$/g, "").trim();
    };

    // Handle editor content change
    const handleEditorChange = (content) => {
      const cleanedContent = cleanContent(content);
      setCachedContent(cleanedContent);
      onChange(cleanedContent);
    };

    // Handle editor initialization
    const handleEditorInit = (evt, editor) => {
      if (editorRef) editorRef.current = editor;

      // If the editor is initialized with empty content but we have cached content, set it
      if (!editor.getContent() && cachedContent) {
        editor.setContent(cachedContent);
      }
    };

    return (
      <Editor
        id={stableId}
        onInit={handleEditorInit}
        apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
        value={cachedContent}
        onEditorChange={handleEditorChange}
        onFocus={onFocus}
        onBlur={onBlur}
        init={{
          height: 500,
          menubar: true,
          plugins: [
            "advlist",
            "autolink",
            "lists",
            "link",
            "image",
            "charmap",
            "preview",
            "anchor",
            "searchreplace",
            "visualblocks",
            "code",
            "fullscreen",
            "insertdatetime",
            "media",
            "table",
            "code",
            "help",
            "wordcount",
            "imagetools",
          ],
          toolbar:
            "undo redo | blocks | " +
            "bold italic forecolor | alignleft aligncenter " +
            "alignright alignjustify | bullist numlist outdent indent | " +
            "image media link | removeformat | help",
          content_style:
            "body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }",
          skin: "oxide",
          content_css: "default",
          branding: false,
          statusbar: true,
          resize: true,
          promotion: false,
          forced_root_block: false,
          remove_trailing_brs: true,
          setup: function (editor) {
            editor.on("BeforeSetContent", function (e) {
              if (e.content) {
                e.content = e.content.replace(/^<p>|<\/p>$/g, "").trim();
              }
            });

            editor.on("GetContent", function (e) {
              if (e.content) {
                e.content = e.content.replace(/^<p>|<\/p>$/g, "").trim();
              }
            });
          },
          images_upload_handler: handleImageUpload,
          automatic_uploads: true,
          file_picker_types: "image",
          image_title: true,
          image_caption: true,
          image_advtab: true,
          image_dimensions: true,
          paste_data_images: true,
          image_toolbar:
            "alignleft aligncenter alignright | rotateleft rotateright | imageoptions",
          file_picker_callback: function (callback, value, meta) {
            if (meta.filetype === "image") {
              const input = document.createElement("input");
              input.setAttribute("type", "file");
              input.setAttribute("accept", "image/*");

              input.onchange = function () {
                const file = this.files[0];
                const reader = new FileReader();
                reader.onload = function () {
                  const id = "blobid" + new Date().getTime();
                  const blobCache = tinymce.activeEditor.editorUpload.blobCache;
                  const base64 = reader.result.split(",")[1];
                  const blobInfo = blobCache.create(id, file, base64);
                  blobCache.add(blobInfo);
                  callback(blobInfo.blobUri(), { title: file.name });
                };
                reader.readAsDataURL(file);
              };

              input.click();
            }
          },
        }}
      />
    );
  },
);

// Define a memoized body section card component to prevent unnecessary re-renders
const MemoizedBodySectionCard = React.memo(
  ({ section, index, onRemove, onChange, provided, isDragging }) => {
    const editorRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const [localContent, setLocalContent] = useState(section.content || "");
    const [errors, setErrors] = useState({});

    useEffect(() => {
      if (!isDragging && section.content !== localContent) {
        setLocalContent(section.content || "");
      }
    }, [section.content, isDragging]);

    const validateSection = (field, value) => {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          [field]: `${field === "name" ? "Section name" : "Content"} is required`,
        }));
        return false;
      }
      setErrors((prev) => ({ ...prev, [field]: "" }));
      return true;
    };

    const handleChange = (field, value) => {
      validateSection(field, value);
      onChange(index, field, value);
    };

    const handleEditorBlur = () => {
      if (editorRef.current) {
        const content = editorRef.current.getContent();
        validateSection("content", content);
        setLocalContent(content);
        onChange(index, "content", content);
        setIsEditing(false);
      }
    };

    const handleEditorFocus = () => {
      setIsEditing(true);
    };

    const cardStyle = isDragging
      ? { opacity: 0.7, boxShadow: "0 0 10px rgba(0,0,0,0.2)" }
      : {};

    const handleDragHandleProps = isEditing ? null : provided.dragHandleProps;

    const handleContentChange = (content) => {
      validateSection("content", content);
      setLocalContent(content);
      onChange(index, "content", content);
    };

    return (
      <div
        className="bg-card rounded-lg shadow-sm p-6 border border-border hover:border-secondary transition-all duration-200"
        {...handleDragHandleProps}
        style={cardStyle}
      >
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`cursor-grab text-muted-foreground hover:text-foreground ${
                isEditing ? "cursor-not-allowed opacity-50" : ""
              }`}
            >
              <GripVertical className="w-5 h-5" />
            </div>
            <h3 className="text-lg font-medium text-foreground">
              Section {index + 1}
            </h3>
          </div>
          {index > 0 && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-500/10 rounded-full transition-colors duration-200"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-foreground text-sm font-medium mb-2">
              Section Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter section name"
              value={section.name}
              onChange={(e) => handleChange("name", e.target.value)}
              onBlur={(e) => validateSection("name", e.target.value)}
              className={`w-full p-3 !bg-gray-50 dark:!bg-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5ba88c] text-foreground ${
                errors.name ? " border-red-500" : ""
              }`}
              required
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>
          <div>
            <label className="block text-foreground text-sm font-medium mb-2">
              Content <span className="text-red-500">*</span>
            </label>
            <TinyMCEWrapper
              value={localContent}
              onChange={handleContentChange}
              editorRef={editorRef}
              onFocus={handleEditorFocus}
              onBlur={handleEditorBlur}
              isDragging={isDragging}
              sectionId={section.id}
              index={index}
            />
            {errors.content && (
              <p className="text-red-500 text-sm mt-1">{errors.content}</p>
            )}
          </div>
        </div>
      </div>
    );
  },
);

// Create a simple draggable component that doesn't re-render unnecessarily
const DraggableItem = React.memo(({ id, index, children }) => {
  return (
    <Draggable draggableId={id} index={index}>
      {(provided, snapshot) => children(provided, snapshot)}
    </Draggable>
  );
});

// Create a simple droppable component that doesn't re-render unnecessarily
const DroppableSection = React.memo(({ id, type, children }) => {
  return (
    <Droppable droppableId={id} type={type}>
      {(provided, snapshot) => children(provided, snapshot)}
    </Droppable>
  );
});

// Completely rewritten CampaignMicrosite component with better memoization
const CampaignMicrosite = React.memo(({ micrositeData, setMicrositeData }) => {
  // Add drag tracking state
  const [isDragging, setIsDragging] = useState(false);
  const [isRearranging, setIsRearranging] = useState(false);
  const [dragSourceIndex, setDragSourceIndex] = useState(null);
  const [dragDestIndex, setDragDestIndex] = useState(null);

  // Cache for section contents during drag operations
  const [contentCache, setContentCache] = useState({});

  // Handler functions
  const handleHeaderChange = (index, field, value) => {
    const updatedHeaders = [...micrositeData.headers];
    updatedHeaders[index] = {
      ...updatedHeaders[index],
      [field]: value,
    };
    setMicrositeData((prev) => ({ ...prev, headers: updatedHeaders }));
  };

  const handleBodyChange = (index, field, value) => {
    const updatedSections = [...micrositeData.bodySections];
    updatedSections[index] = {
      ...updatedSections[index],
      [field]: value,
    };
    setMicrositeData((prev) => ({ ...prev, bodySections: updatedSections }));

    // Also update the content cache
    if (field === "content") {
      setContentCache((prev) => ({
        ...prev,
        [updatedSections[index].id || `temp-${index}`]: value,
      }));
    }
  };

  const handleRemoveHeader = (index) => {
    setMicrositeData((prev) => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveBodySection = (index) => {
    setMicrositeData((prev) => ({
      ...prev,
      bodySections: prev.bodySections.filter((_, i) => i !== index),
    }));
  };

  const handleAddHeader = () => {
    setMicrositeData((prev) => ({
      ...prev,
      headers: [...prev.headers, { label: "", value: "" }],
    }));
  };

  const handleAddBodySection = () => {
    setMicrositeData((prev) => ({
      ...prev,
      bodySections: [...prev.bodySections, { name: "", content: "" }],
    }));
  };

  // Track when drag starts to handle TinyMCE visibility
  const onDragStart = (start) => {
    setIsDragging(true);
    setDragSourceIndex(start.source.index);

    // Cache all current editor contents
    if (start.type === "BODY_SECTIONS") {
      const newCache = {};
      micrositeData.bodySections.forEach((section, idx) => {
        const id = section.id || `temp-${idx}`;
        newCache[id] = section.content;
      });
      setContentCache(newCache);
    }
  };

  // Modified drag end handler to better preserve TinyMCE content
  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    // Reset drag state even if no destination
    setIsDragging(false);
    setDragSourceIndex(null);

    // Drop was canceled or dropped in the same position
    if (
      !destination ||
      (source.index === destination.index &&
        source.droppableId === destination.droppableId)
    ) {
      return;
    }

    // Set rearranging flag to prevent editor mount/unmount issues
    setIsRearranging(true);
    setDragDestIndex(destination.index);

    // Handle headers reordering
    if (type === "HEADERS") {
      const newHeaders = reorder(
        micrositeData.headers,
        source.index,
        destination.index,
      );

      setMicrositeData((prev) => ({
        ...prev,
        headers: newHeaders,
      }));
    }
    // Handle body sections reordering with special care for TinyMCE content
    else if (type === "BODY_SECTIONS") {
      // Make a copy of the current sections
      const currentSections = [...micrositeData.bodySections];

      // Save content of the section being dragged
      const draggedSection = { ...currentSections[source.index] };
      const draggedId = draggedSection.id || `temp-${source.index}`;

      // Ensure we store the most recent content from the cache
      draggedSection.content =
        contentCache[draggedId] || draggedSection.content;

      // Remove the dragged item
      currentSections.splice(source.index, 1);

      // Insert at new position
      currentSections.splice(destination.index, 0, draggedSection);

      // Update the state with re-ordered sections, ensuring content is preserved
      setTimeout(() => {
        setMicrositeData((prev) => ({
          ...prev,
          bodySections: currentSections.map((section, idx) => {
            const sectionId = section.id || `temp-${idx}`;
            // Preserve the cached content for each section
            return {
              ...section,
              content: contentCache[sectionId] || section.content,
            };
          }),
        }));

        // Reset the rearranging flag after a short delay
        setTimeout(() => {
          setIsRearranging(false);
          setDragDestIndex(null);
        }, 100);
      }, 50);
    }
  };

  // Generate stable keys for draggable items
  const getHeaderId = (header, index) => {
    return header.id ? `header-${header.id}` : `header-new-${index}`;
  };

  const getBodySectionId = (section, index) => {
    return section.id ? `section-${section.id}` : `section-new-${index}`;
  };

  // Define custom drag styles for bottom-to-top drag animations
  const getDragStyle = (isDragging, draggableStyle) => ({
    // Basic styles to make items appear correctly during drag
    userSelect: "none",
    // Apply the draggableStyle provided by react-beautiful-dnd
    ...draggableStyle,
    // Add additional styles when dragging
    ...(isDragging && {
      boxShadow: "0 5px 10px rgba(0, 0, 0, 0.15)",
      zIndex: 1000,
      // This will ensure the dragged item appears above others
      position: draggableStyle.position,
    }),
  });

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
      <div className="space-y-8 bg-card py-6 rounded-lg shadow-sm">
        {/* Headers Section */}
        <div className="space-y-6 bg-muted p-6 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Headers</h2>
              <p className="text-sm text-foreground mt-1">
                Add and reorder headers for your campaign
              </p>
            </div>
          </div>

          <DroppableSection id="headers-droppable" type="HEADERS">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`grid gap-4 mt-4 ${
                  snapshot.isDraggingOver ? "bg-[#edf7f2]" : ""
                }`}
                style={{ minHeight: "50px" }} // Ensure there's always space for dropping
              >
                {micrositeData.headers.map((header, index) => {
                  const headerId = getHeaderId(header, index);
                  return (
                    <DraggableItem key={headerId} id={headerId} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={getDragStyle(
                            dragSnapshot.isDragging,
                            dragProvided.draggableProps.style,
                          )}
                        >
                          <MemoizedHeaderCard
                            header={header}
                            index={index}
                            onRemove={handleRemoveHeader}
                            onChange={handleHeaderChange}
                            provided={{
                              ...dragProvided,
                              dragHandleProps: dragProvided.dragHandleProps,
                              innerRef: () => {}, // This prevents the double ref issue
                            }}
                            isDragging={dragSnapshot.isDragging}
                          />
                        </div>
                      )}
                    </DraggableItem>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </DroppableSection>

          <div className="flex items-center mt-4">
            <button
              type="button"
              onClick={handleAddHeader}
              className="flex items-center gap-2 px-4 py-2 bg-primary-dark hover:brightness-110 transition-colors text-white rounded-lg font-medium"
            >
              <Plus className="w-5 h-5" />
              Add Header
            </button>
          </div>
        </div>

        {/* Body Sections */}
        <div className="space-y-6 bg-muted p-6 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Body Sections</h2>
              <p className="text-sm text-foreground mt-1">
                Add and reorder content sections for your campaign
              </p>
            </div>
          </div>

          <DroppableSection id="body-sections-droppable" type="BODY_SECTIONS">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`grid gap-4 mt-4 ${
                  snapshot.isDraggingOver ? "bg-[#edf7f2]" : ""
                }`}
                style={{ minHeight: "50px" }} // Ensure there's always space for dropping
              >
                {micrositeData.bodySections.map((section, index) => {
                  const sectionId = getBodySectionId(section, index);
                  // Determine if this specific section is being dragged or is at the destination
                  const isSectionDragging =
                    isDragging && dragSourceIndex === index;
                  const isSectionRearranging =
                    isRearranging &&
                    (index === dragDestIndex || index === dragSourceIndex);

                  return (
                    <DraggableItem key={sectionId} id={sectionId} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          style={getDragStyle(
                            dragSnapshot.isDragging,
                            dragProvided.draggableProps.style,
                          )}
                        >
                          <MemoizedBodySectionCard
                            section={section}
                            index={index}
                            onRemove={handleRemoveBodySection}
                            onChange={handleBodyChange}
                            provided={{
                              ...dragProvided,
                              dragHandleProps: dragProvided.dragHandleProps,
                              innerRef: () => {}, // This prevents the double ref issue
                            }}
                            isDragging={
                              dragSnapshot.isDragging || isSectionRearranging
                            }
                          />
                        </div>
                      )}
                    </DraggableItem>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </DroppableSection>

          <div className="flex items-center mt-4">
            <button
              type="button"
              onClick={handleAddBodySection}
              className="flex items-center gap-2 px-4 py-2 bg-primary-dark text-white rounded-lg hover:brightness-110 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              Add Section
            </button>
          </div>
        </div>
      </div>
    </DragDropContext>
  );
});

const CampaignForm = ({ readOnly, setActiveTab }) => {
  const { companyId, id } = useParams();
  const { showToast } = useToastStore;

  const navigate = useNavigate();
  const { mutate: createCampaign, isPending: isCreatingCampaign } =
    useCreateCampaign({
      onSuccess: (response) => {
        navigate(`/companies/view/${response.campaign.company_id}`, {
          state: { activeTab: "campaigns" },
        });
      },
    });
  const { mutate: updateCampaign, isPending: isUpdatingCampaign } =
    useUpdateCampaign({
      onSuccess: (response, { shouldRedirect = true }) => {
        if (shouldRedirect) {
          navigate(`/companies/view/${response.campaign.company_id}`, {
            state: { activeTab: "campaigns" },
          });
        }
      },
    });

  const { data: campaignData, isLoading: isLoadingCampaign } = useGetCampaign(
    {
      enabled: !!id,
    },
    id,
  );

  const { data: companyData, isLoading: isCompanyLoading } = useCompanyById(
    companyId,
    {
      enabled: !!companyId && companyId !== "undefined",
    },
  );

  // Debug logging to track API calls
  console.log(
    "CampaignForm render - companyId:",
    companyId,
    "enabled:",
    !!companyId && companyId !== "undefined",
  );
  // console.log(id, "id");
  // console.log(campaignData, "campaignData");

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    slugManual: false,
    slugGenerating: false,
    slugValid: true,
    slugAvailable: true,
    slugMessage: "",
    clientId: "",
    recruiters: [],
    campaignEmail: "",
    isWhiteLabel: false,
    show_top: false,
    isAnonymizedView: false,
    customUrl: "",
    customEmail: "",
    hasMicrosite: false,
    logo: "",
    content: "",
    status: "", // 'draft' or 'published'
    selectedTheme: "", // New field for page theme
    startDate: "", // Add start date
    endDate: "", // Add end date
    startTime: "",
    endTime: "",
    preCampaignText: "",
    campaignEndMessage: "",
    preTextTitle: "", // Add pre_text_title field
    postTextTitle: "", // Add post_text_title field
  });

  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [recruiterSearch, setRecruiterSearch] = useState("");
  const [filteredRecruiters, setFilteredRecruiters] = useState([]);
  const { data: recruiters = [], isLoading: isRecruitersLoading } =
    useAllRecruiters();

  // Page Themes
  const pageThemes = [
    { id: 1, name: "Corporate Blue" },
    { id: 2, name: "Modern Green" },
    { id: 3, name: "Classic White" },
  ];

  // Add these states if not already present
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Preview modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Add this effect to handle clicking outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Load companies data
  useEffect(() => {
    // Only run this effect if we have a valid companyId and companyData
    if (!companyId || companyId === "undefined" || !companyData) {
      return;
    }

    const loadCompanies = async () => {
      console.log("company", companyId);
      // const storedCompanies = JSON.parse(
      //   localStorage.getItem("companies") || "[]"
      // );
      // setCompanies(storedCompanies);

      // If we have a companyId from URL, find and set that company
      if (companyData.company) {
        setSelectedCompany(companyData.company);
        setFormData((prev) => ({
          ...prev,
          clientId: companyData.company.id,
          customUrl: `${companyData.company.name
            .toLowerCase()
            .replace(/\s+/g, "")}.apptrkr.com`,
        }));
      }
    };
    loadCompanies();
  }, [companyId, companyData]);

  // Load campaign data if we're in view/edit mode
  useEffect(() => {
    if (id) {
      const storedCampaigns = JSON.parse(
        localStorage.getItem("campaigns") || "[]",
      );
      const campaign = storedCampaigns.find((c) => c.id === id);
      if (campaign) {
        setFormData(campaign);
      }
    }
  }, [id]);

  const [raw_campaign, setRawCampaign] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (raw_campaign) {
      // const campaign_slug = formData.title.toLowerCase().replace(/\s+/g, "-");
      // console.log("raw_url", `/${raw_campaign?.company?.slug}/${raw_campaign?.slug}`);s
      setPreviewUrl(`/${raw_campaign?.company?.slug}/${raw_campaign?.slug}`);
    }
  }, [raw_campaign]);
  useEffect(() => {
    if (campaignData?.data) {
      setRawCampaign(campaignData?.data);
      setFormData({
        title: campaignData?.data?.title || "",
        slug: campaignData?.data?.slug || "",
        slugManual: false,
        slugGenerating: false,
        slugValid: validateSlugFormat(campaignData?.data?.slug || ""),
        slugAvailable: true,
        slugMessage: "",
        clientId: campaignData?.data?.company_id || "",
        recruiters: [],
        campaignEmail: campaignData?.data?.email || "",
        isWhiteLabel: false,
        show_top:
          campaignData?.data?.show_top === true ||
          campaignData?.data?.show_top === 1 ||
          campaignData?.data?.show_top === "1",
        isAnonymizedView: campaignData?.data?.anonymized || false,
        customUrl: campaignData?.data?.custom_url || "",
        customEmail: campaignData?.data?.custom_email || "",
        logo: campaignData?.data?.company?.logo?.url || "",
        content: "",
        status: campaignData?.data?.status === "1" ? "published" : "draft", // 'draft' or 'published'
        selectedTheme: "", // New field for page theme
        startDate: campaignData?.data?.start_date || "", // Add start date
        endDate: campaignData?.data?.end_date || "", // Add end date
        timezone: campaignData?.data?.time_zone || "",
        startTime: campaignData?.data?.start_time
          ? campaignData?.data?.start_time.split(":").slice(0, 2).join(":")
          : "",
        endTime: campaignData?.data?.end_time
          ? campaignData?.data?.end_time.split(":").slice(0, 2).join(":")
          : "",
        preCampaignText: campaignData?.data?.pre_campaign_text || "",
        campaignEndMessage: campaignData?.data?.end_campaign_text || "",
        preTextTitle: campaignData?.data?.pre_text_title || "", // Add pre_text_title
        postTextTitle: campaignData?.data?.post_text_title || "", // Add post_text_title
        hasMicrosite: campaignData?.data?.has_microsite || false,
      });

      // setMicrositeData(campaignData?.data?.fields || []);
      // Separate fields into headers and bodySections
      const headers = campaignData?.data?.campaign_fields?.filter(
        (field) => field.field_type == "heading",
      );
      const bodySections = campaignData?.data?.campaign_fields?.filter(
        (field) => field.field_type == "body",
      );
      // console.warn("fields", campaignData?.data?.fields);
      // console.warn( "headers", headers);
      // console.warn("bodySections", bodySections);

      setMicrositeData({
        headers: headers.map((header) => ({
          label: header.field_title,
          value: header.field_text,
          id: header.id,
        })),
        bodySections: bodySections.map((section) => ({
          name: section.field_title,
          content: section.field_text,
          id: section.id,
        })),
      });
    }
  }, [campaignData]);
  // Handle company selection
  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    const selected = companies.find((company) => company.id === companyId);
    setSelectedCompany(selected);
    setFormData((prev) => ({
      ...prev,
      clientId: companyId,
      customUrl: selected
        ? `${selected.name.toLowerCase().replace(/\s+/g, "")}.apptrkr.com`
        : "",
    }));
  };

  // Load company data when a client is selected
  useEffect(() => {
    if (selectedCompany) {
      setFormData((prev) => ({
        ...prev,
        logo: selectedCompany.logo,
        customUrl: `${selectedCompany.name
          .toLowerCase()
          .replace(/\s+/g, "")}.apptrkr.com`,
      }));
      setLogoPreview(selectedCompany.logo);
    }
  }, [selectedCompany]);

  // Update recruiter search functionality
  useEffect(() => {
    if (recruiterSearch) {
      const filtered = recruiters.filter((recruiter) =>
        recruiter.name.toLowerCase().includes(recruiterSearch.toLowerCase()),
      );
      setFilteredRecruiters(filtered);
    } else {
      setFilteredRecruiters(recruiters);
    }
  }, [recruiterSearch, recruiters]);

  // Handle recruiter selection
  const handleRecruiterChange = (recruiterId) => {
    setFormData((prev) => {
      const newRecruiters = prev.recruiters.includes(recruiterId)
        ? prev.recruiters.filter((id) => id !== recruiterId)
        : [...prev.recruiters, recruiterId];
      return { ...prev, recruiters: newRecruiters };
    });
  };

  // Add micrositeData state to CampaignForm
  const [micrositeData, setMicrositeData] = useState({
    headers: [{ label: "", value: "" }],
    bodySections: [{ name: "", content: "" }],
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Auto-generate slug when title is set but slug is empty (for new forms)
  useEffect(() => {
    if (formData.title && !formData.slug && !formData.slugManual) {
      const newSlug = generateSlug(formData.title);
      const isValid = validateSlugFormat(newSlug);
      setFormData((prev) => ({
        ...prev,
        slug: newSlug,
        slugValid: isValid,
        slugAvailable: true,
        slugMessage: "",
      }));
    }
  }, [formData.title, formData.slug, formData.slugManual]);

  const debouncedSlugCheck = useDebounce(async (slugValue) => {
    if (slugValue && formData.slugManual) {
      try {
        setFormData((prev) => ({
          ...prev,
          slugGenerating: true,
          slugMessage: "",
        }));

        const result = await slugService.checkCampaignSlugAvailability(
          slugValue,
          id || null,
        );

        const hasErrors =
          result.errors ||
          result.status === "error" ||
          result.success === false ||
          result.message?.toLowerCase().includes("taken") ||
          result.message?.toLowerCase().includes("already");
        const isSuccess =
          !hasErrors &&
          (result.success === true ||
            result.status === "success" ||
            (!result.errors &&
              !result.message?.toLowerCase().includes("taken") &&
              !result.message?.toLowerCase().includes("already")));

        let message = "";
        if (
          result.errors &&
          result.errors.slug &&
          result.errors.slug.length > 0
        ) {
          message = result.errors.slug[0];
        } else if (result.errors && typeof result.errors === "string") {
          message = result.errors;
        } else if (result.message) {
          message = result.message;
        } else if (result.msg) {
          message = result.msg;
        } else if (isSuccess) {
          message = "Slug is available";
        }

        if (isSuccess) {
          setFormData((prev) => ({
            ...prev,
            slugGenerating: false,
            slugValid: validateSlugFormat(slugValue),
            slugAvailable: true,
            slugMessage: message,
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            slugGenerating: false,
            slugValid: validateSlugFormat(slugValue),
            slugAvailable: false,
            slugMessage: message,
          }));
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to check slug availability";
        setFormData((prev) => ({
          ...prev,
          slugGenerating: false,
          slugValid: validateSlugFormat(prev.slug),
          slugAvailable: false,
          slugMessage: errorMessage,
        }));
      }
    }
  }, 2000);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "slug") {
      let processedValue = generateSlug(value);

      if (processedValue.length > 100) {
        processedValue = processedValue.substring(0, 100);
      }

      const isValid = validateSlugFormat(processedValue);

      setFormData((prev) => ({
        ...prev,
        [name]: processedValue,
        slugValid: isValid,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value || "",
    }));
  };

  // Add validation on blur
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  // Validate individual field
  const validateField = (field) => {
    const newErrors = { ...errors };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    switch (field) {
      case "title":
        if (!formData.title?.trim()) {
          newErrors.title = "Campaign title is required";
        } else {
          delete newErrors.title;
        }
        break;

      case "campaignEmail":
        if (!formData.campaignEmail) {
          newErrors.campaignEmail = "Campaign email is required";
        } else if (!emailRegex.test(formData.campaignEmail)) {
          newErrors.campaignEmail = "Please enter a valid email address";
        } else {
          delete newErrors.campaignEmail;
        }
        break;

      case "startDate":
        if (!formData.startDate) {
          newErrors.startDate = "Start date is required";
        } else {
          delete newErrors.startDate;
        }
        break;

      case "startTime":
        if (!formData.startTime) {
          newErrors.startTime = "Start time is required";
        } else {
          delete newErrors.startTime;
        }
        break;

      case "endDate":
        if (!formData.endDate) {
          newErrors.endDate = "End date is required";
        } else if (
          formData.startDate &&
          formData.endDate < formData.startDate
        ) {
          newErrors.endDate = "End date must be after start date";
        } else {
          delete newErrors.endDate;
        }
        break;

      case "endTime":
        if (!formData.endTime) {
          newErrors.endTime = "End time is required";
        } else {
          delete newErrors.endTime;
        }
        break;

      case "time_zone":
        if (!formData.timezone) {
          newErrors.timezone = "Time zone is required";
        } else {
          delete newErrors.timezone;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add these states near other state declarations
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);

  // Modify the handleSubmit function
  const handleSubmit = async (e, status) => {
    e.preventDefault();
    const shouldRedirect = status !== "preview"; // !== is for true
    const openPreview = status === "preview"; // === is for true

    // Validate microsite sections

    const hasHeaderErrors = micrositeData.headers.some(
      (header) => !header.value || !header.value.trim(),
    );

    const hasBodyErrors = micrositeData.bodySections.some(
      (section) =>
        !section.name ||
        !section.name.trim() ||
        !section.content ||
        !section.content.trim(),
    );

    if (hasBodyErrors) {
      showToast({
        message: "Please fill in all required fields in the body section",
        type: "error",
      });
      return;
    }

    // Set appropriate loading state
    if (openPreview) setIsPreviewing(true);
    if (status === "draft") {
      if (!openPreview) setIsSavingDraft(true); // prevent loading state when previewing
    } else if (id) {
      if (!openPreview) setIsUpdating(true); // prevent loading state when previewing
    } else {
      if (!openPreview) setIsCreating(true); // prevent loading state when previewing
    }

    // Mark all required fields as touched
    const requiredFields = [
      "title",
      "campaignEmail",
      "timezone",
      "startDate",
      "startTime",
      "endDate",
      "endTime",
    ];
    const newTouched = requiredFields.reduce(
      (acc, field) => ({ ...acc, [field]: true }),
      {},
    );
    setTouched(newTouched);

    // Validate all required fields
    let isValid = true;
    requiredFields.forEach((field) => {
      if (!validateField(field)) {
        isValid = false;
      }
    });

    if (!isValid) {
      showToast({
        message: "Please fill in all required fields correctly",
        type: "error",
      });
      setIsUpdating(false);
      setIsCreating(false);
      setIsSavingDraft(false);
      return;
    }

    const payload = {
      company_id: companyId,
      title: formData.title,
      slug: formData.slug,
      slugManual: formData.slugManual,
      email: formData.campaignEmail,
      description: formData.content || "Description not provided",
      custom_email: formData.customEmail,
      custom_url: formData.customUrl,
      start_date: formData.startDate,
      start_time: formData.startTime,
      time_zone: formData.timezone,
      end_date: formData.endDate,
      end_time: formData.endTime,
      status: status === "draft" || status === "preview" ? 0 : 1, // 0 is draft, 1 is published
      pre_campaign_text: formData.preCampaignText || "",
      end_campaign_text: formData.campaignEndMessage || "",
      pre_text_title: formData.preTextTitle || "", // Add pre_text_title
      show_top: Boolean(formData.show_top),
      post_text_title: formData.postTextTitle || "", // Add post_text_title
      anonymized: formData.isAnonymizedView,
      fields: [
        ...micrositeData.headers
          .filter((header) => header.label || header.value)
          .map((header, index) => ({
            field_type: "heading",
            field_title: header.label,
            field_text: header.value,
            field_order: index + 1,
          })),
        ...micrositeData.bodySections
          .filter((section) => section.name || section.content)
          .map((section, index) => ({
            field_type: "body",
            field_title: section.name,
            field_text: section.content,
            field_order: micrositeData.headers.length + index + 1,
          })),
      ],
    };

    if (formData.logo) {
      payload.image = formData.logo;
    }

    if (id) {
      await updateCampaign(
        { id, campaignData: payload, shouldRedirect: shouldRedirect },
        {
          onSuccess: (response) => {
            setIsPreviewing(false);
            if (status == "draft") {
              setIsSavingDraft(false);
            } else {
              setIsUpdating(false);
            }
            const successMessage =
              response?.message ||
              `Campaign ${
                status === "draft" ? "saved as draft" : "published"
              } successfully!`;
            showToast({
              message: successMessage,
              type: "success",
            });
            if (openPreview) {
              window.open(previewUrl, "_blank");
            }
          },
          onError: (error) => {
            setIsPreviewing(false);
            if (status == "draft") {
              setIsSavingDraft(false);
            } else {
              setIsUpdating(false);
            }
            // Check if error has the expected structure
            if (error.errors) {
              // Display each validation error as a separate toast
              Object.entries(error.errors).forEach(([field, messages]) => {
                const readableField = field
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ");

                messages.forEach((message) => {
                  showToast({
                    message: `${readableField}: ${message}`,
                    type: "error",
                  });
                });
              });
            } else if (error.message) {
              // Display the general error message
              showToast({
                message: error.message,
                type: "error",
              });
            } else {
              // Fallback error message
              showToast({
                message: "An error occurred while creating the campaign.",
                type: "error",
              });
            }
          },
        },
      );
    } else {
      await createCampaign(payload, {
        onSuccess: (response) => {
          setIsPreviewing(false);
          if (status == "draft") {
            setIsSavingDraft(false);
          } else {
            setIsCreating(false);
          }
          const successMessage =
            response?.message ||
            `Campaign ${
              status === "draft" ? "saved as draft" : "published"
            } successfully!`;
          showToast({
            message: successMessage,
            type: "success",
          });
          if (openPreview) {
            window.open(previewUrl, "_blank");
          }
        },
        onError: (error) => {
          setIsPreviewing(false);
          if (status == "draft") {
            setIsSavingDraft(false);
          } else {
            setIsCreating(false);
          }
          // Check if error has the expected structure
          if (error.errors) {
            // Display each validation error as a separate toast
            Object.entries(error.errors).forEach(([field, messages]) => {
              const readableField = field
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              messages.forEach((message) => {
                showToast({
                  message: `${readableField}: ${message}`,
                  type: "error",
                });
              });
            });
          } else if (error.message) {
            // Display the general error message
            showToast({
              message: error.message,
              type: "error",
            });
          } else {
            // Fallback error message
            showToast({
              message: "An error occurred while creating the campaign.",
              type: "error",
            });
          }
        },
      });
    }
  };

  // handle preview save
  // const handlePreviewSave = (e, status) => {
  //   {
  //     e.preventDefault();
  //     const payload = {
  //       company_id: companyId,
  //       title: formData.title,
  //       email: formData.campaignEmail,
  //       description: formData.content || "Description not provided",
  //       custom_email: formData.customEmail,
  //       custom_url: formData.customUrl,
  //       start_date: formData.startDate,
  //       start_time: formData.startTime,
  //       time_zone: formData.timezone,
  //       end_date: formData.endDate,
  //       end_time: formData.endTime,
  //       status: 0,
  //       pre_campaign_text: formData.preCampaignText || "",
  //       end_campaign_text: formData.campaignEndMessage || "",
  //       pre_text_title: formData.preTextTitle || "",
  //       post_text_title: formData.postTextTitle || "",
  //       anonymized: formData.isAnonymizedView,
  //       fields: [
  //         ...micrositeData.headers
  //           .filter((header) => header.label || header.value)
  //           .map((header, index) => ({
  //             field_type: "heading",
  //             field_title: header.label,
  //             field_text: header.value,
  //             field_order: index + 1,
  //           })),
  //         ...micrositeData.bodySections
  //           .filter((section) => section.name || section.content)
  //           .map((section, index) => ({
  //             field_type: "body",
  //             field_title: section.name,
  //             field_text: section.content,
  //             field_order: micrositeData.headers.length + index + 1,
  //           })),
  //       ],
  //     };

  //     if (formData.logo) {
  //       payload.image = formData.logo;
  //     }

  //     updateCampaign(
  //       { id, campaignData: payload, shouldRedirect: false },
  //       {
  //         onSuccess: (response) => {
  //           showToast({
  //             message: "Data previewed successfully",
  //             type: "success"
  //           });
  //           navigate(`/campaigns/${id}`);
  //         },
  //         onError: (error) => {
  //           showToast({
  //             message: error.message || "An error occurred while previewing",
  //             type: "error"
  //           });
  //         }
  //       }
  //     );
  //   }
  // };

  // Add these states at the top of your component
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const clientDropdownRef = useRef(null);

  // Add this effect for client dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        clientDropdownRef.current &&
        !clientDropdownRef.current.contains(event.target)
      ) {
        setIsClientDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const today = new Date().toISOString().split("T")[0];

  // Add expandedSection state
  const [expandedSection, setExpandedSection] = useState(null);

  // Remove the redundant handleDragEnd function and fieldValues state that cause re-rendering issues
  const [fieldValue, setFieldValue] = useState({});

  const FIXED_EMAIL_DOMAIN = "dartmouthpartners.com";

  // derive just the local‐part
  const localPart = (formData.campaignEmail || "").split("@")[0];

  const handleBackToCampaigns = () => {
    const companyIdToUse =
      companyId || formData.clientId || selectedCompany?.id;
    if (!companyIdToUse) {
      console.error("Company ID is undefined");
      return;
    }
    navigate(`/companies/view/${companyIdToUse}`, {
      state: { activeTab: "campaigns" },
    });
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      
      <div className="flex-1 w-[90%] py-8 px-8 mx-auto flex flex-col gap-8">
        <div className="flex flex-col gap-6">
          <PageHeader
            navigationText="Back to Campaigns"
            navigateTo={handleBackToCampaigns}
            customClasses="!gap-0"
          />
          {/* Header Section */}
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div className="flex flex-col gap-1">
                <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                  {id ? "Update Campaign" : "Create New Campaign"}
                </h1>
                <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                  {id
                    ? "Modify existing campaign details, microsite configuration, and timing settings."
                    : "Fill in the details below to create and publish a new recruitment campaign."}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* General Details */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground mb-4">
                General Details
              </h2>

              {/* Title Field */}
              <div className="space-y-2">
                <label htmlFor="title" className="block text-foreground font-medium">
                  Campaign Title <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                  <FileText className="w-5 h-5 text-foreground" />
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={(e) => {
                      handleInputChange(e);
                      if (!formData.slugManual) {
                        const newSlug = generateSlug(e.target.value);
                        const isValid = validateSlugFormat(newSlug);
                        setFormData((prev) => ({
                          ...prev,
                          slug: newSlug,
                          slugValid: isValid,
                          slugAvailable: true,
                          slugMessage: "",
                        }));
                      }
                      if (touched.title) {
                        validateField("title");
                      }
                    }}
                    onBlur={() => handleBlur("title")}
                    className={`bg-transparent w-full focus:outline-none text-foreground ${
                      errors.title ? "border-b-2 border-red-500" : ""
                    }`}
                    placeholder="Enter campaign title"
                    required
                  />
                </div>
                {touched.title && errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>
              {/* Slug Field */}

              <div className="space-y-2">
                    <label
                      htmlFor="slug"
                      className="block text-foreground font-medium"
                    >
                      Slug <span className="text-muted-foreground text-sm"></span>
                    </label>

                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <LinkIcon className="w-5 h-5 text-foreground" />
                      <input
                        type="text"
                        id="slug"
                        name="slug"
                        value={formData.slug}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Mark as manually edited when user changes the slug
                          if (!formData.slugManual) {
                            setFormData((prev) => ({
                              ...prev,
                              slugManual: true,
                            }));
                          }
                          // Call debounced slug availability check when manually entered
                          debouncedSlugCheck(e.target.value);
                        }}
                        className={`bg-transparent w-full focus:outline-none text-foreground ${formData.slugManual ? "border-l-4  pl-2" : ""} ${!formData.slugValid && formData.slug ? "border-b-2 border-red-500" : ""}`}
                        placeholder="Auto-generated from title"
                      />
                      {formData.slugGenerating && (
                        <Loader2 className="w-4 h-4 text-foreground animate-spin" />
                      )}
                      {!formData.slugValid && formData.slug && (
                        <XCircle
                          className="w-4 h-4 text-red-500"
                          title="Invalid slug format"
                        />
                      )}
                      {formData.slugValid &&
                        formData.slug &&
                        !formData.slugGenerating &&
                        !formData.slugAvailable && (
                          <XCircle
                            className="w-4 h-4 text-red-500"
                            title="API validation failed"
                          />
                        )}
                      {formData.slugValid &&
                        formData.slug &&
                        formData.slugAvailable &&
                        !formData.slugGenerating && (
                          <CheckCircle2
                            className="w-4 h-4 text-green-500"
                            title="API validation successful"
                          />
                        )}
                    </div>
                    {!formData.slugValid && formData.slug && (
                      <p className="text-red-500 text-sm mt-1">
                        Slug must contain only lowercase letters, numbers, and
                        single dashes. Cannot start or end with a dash.
                      </p>
                    )}
                    {formData.slugValid &&
                      formData.slug &&
                      !formData.slugAvailable &&
                      formData.slugMessage && (
                        <p className="text-red-500 text-sm mt-1">
                          {formData.slugMessage}
                        </p>
                      )}
                    {formData.slugValid &&
                      formData.slug &&
                      formData.slugAvailable &&
                      formData.slugMessage && (
                        <p className="text-green-500 text-sm mt-1">
                          {formData.slugMessage}
                        </p>
                      )}
                    {/* Show message even if slugValid is false but we have a message from API */}
                    {!formData.slugValid &&
                      formData.slug &&
                      formData.slugMessage && (
                        <p className="text-red-500 text-sm mt-1">
                          {formData.slugMessage}
                        </p>
                      )}
                  </div>

              {/* Contact ID Field */}

              {/* Client Selection */}
              <div className="space-y-2">
                <div className="relative" ref={clientDropdownRef}>
                  {/* Client Dropdown Menu */}
                  {isClientDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-card rounded-lg shadow-lg border border-border max-h-[200px] overflow-y-auto">
                      {companies.filter((company) =>
                        company.name
                          .toLowerCase()
                          .includes(clientSearch.toLowerCase()),
                      ).length > 0 ? (
                        companies
                          .filter((company) =>
                            company.name
                              .toLowerCase()
                              .includes(clientSearch.toLowerCase()),
                          )
                          .map((company) => (
                            <div
                              key={company.id}
                              className={`
                                px-4 py-2 cursor-pointer text-sm
                                ${
                                  selectedCompany?.id === company.id
                                    ? "bg-secondary-soft text-foreground"
                                    : "hover:bg-muted"
                                }
                              `}
                              onClick={() => {
                                setSelectedCompany(company);
                                setFormData((prev) => ({
                                  ...prev,
                                  clientId: company.id,
                                  customUrl: `${company.name
                                    .toLowerCase()
                                    .replace(/\s+/g, "")}.apptrkr.com`,
                                }));
                                setIsClientDropdownOpen(false);
                                setClientSearch("");
                              }}
                            >
                              {company.name}
                            </div>
                          ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                          No companies found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Campaign Email */}
              <div className="space-y-2">
                <label
                  htmlFor="campaignEmailLocal"
                  className="block text-foreground font-medium"
                >
                  Campaign Email <span className="text-red-500">*</span>
                </label>
                <p className="text-foreground text-sm">
                  Automatic replies will be sent from this email address.
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex items-center w-full space-x-0">
                    {/* input for the local part only */}
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <Mail className="w-5 h-5 text-foreground mr-2" />
                      <input
                        type="text"
                        id="campaignEmailLocal"
                        value={localPart}
                        size={Math.min(Math.max(localPart.length, 5), 100)}
                        onChange={(e) => {
                          const local = e.target.value.replace(/@.*/, "");
                          setFormData((prev) => ({
                            ...prev,
                            campaignEmail: local
                              ? `${local}@${FIXED_EMAIL_DOMAIN}`
                              : "",
                          }));
                          if (touched.campaignEmail)
                            validateField("campaignEmail");
                        }}
                        onBlur={() => {
                          setTouched((prev) => ({
                            ...prev,
                            campaignEmail: true,
                          }));
                          validateField("campaignEmail");
                        }}
                        className={`
                            bg-transparent focus:outline-none text-foreground
                            ${errors.campaignEmail ? "border-b-2 border-red-500" : ""}
                          `}
                        placeholder="Campaign Email"
                        required
                        style={{
                          width: "auto",
                          paddingRight: 0,
                          marginRight: "-2px", // slight negative margin to remove the gap
                        }}
                      />
                      <span className="text-foreground">@{FIXED_EMAIL_DOMAIN}</span>
                    </div>
                  </div>
                </div>
                {touched.campaignEmail && errors.campaignEmail && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.campaignEmail}
                  </p>
                )}
              </div>

              {/* Campaign Timing Section */}
              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-3 gap-4">
                  {/* Time Zone */}
                  <div className="space-y-2">
                    <label className="block text-foreground font-medium">
                      Time Zone <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                      options={TIMEZONES}
                      // onChange={(selected) => setFieldValue(selected.value)}
                      onChange={(selected) => {
                        setFormData((prev) => ({
                          ...prev,
                          timezone: selected.value,
                        }));
                        if (touched.timezone) {
                          validateField("timezone");
                        }
                      }}
                      placeholder="Select Time Zone"
                      value={formData.timezone || ""}
                      searchable={true}
                      // dropdownWidth='w-full max-w-[22rem]'
                    />
                    {touched.timezone && errors.timezone && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.timezone}
                      </p>
                    )}
                  </div>

                  {/* Start Time */}
                  <div className="space-y-2">
                    <label className="block text-foreground font-medium">
                      Start Time <span className="text-red-400">*</span>
                    </label>
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <Clock className="w-5 h-5 text-foreground" />
                      <select
                        value={formData.startTime || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            startTime: e.target.value,
                          }));
                          if (touched.startTime) {
                            validateField("startTime");
                          }
                        }}
                        onBlur={() => handleBlur("startTime")}
                        className={`bg-transparent w-full focus:outline-none text-foreground ${
                          errors.startTime ? "border-b-2 border-red-500" : ""
                        }`}
                      >
                        <option value="">Select time</option>
                        {Array.from({ length: 96 }).map((_, index) => {
                          const hours = Math.floor(index / 4);
                          const minutes = (index % 4) * 15;
                          const time = `${hours
                            .toString()
                            .padStart(2, "0")}:${minutes
                            .toString()
                            .padStart(2, "0")}`;
                          return (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    {touched.startTime && errors.startTime && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.startTime}
                      </p>
                    )}
                  </div>

                  {/* End Time */}
                  <div className="space-y-2">
                    <label className="block text-foreground font-medium">
                      End Time <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <Clock className="w-5 h-5 text-foreground" />
                      <select
                        value={formData.endTime || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            endTime: e.target.value,
                          }));
                          if (touched.endTime) {
                            validateField("endTime");
                          }
                        }}
                        onBlur={() => handleBlur("endTime")}
                        className={`bg-transparent w-full focus:outline-none text-foreground ${
                          errors.endTime ? "border-b-2 border-red-500" : ""
                        }`}
                        required
                      >
                        <option value="">Select time</option>
                        {Array.from({ length: 96 }).map((_, index) => {
                          const hours = Math.floor(index / 4);
                          const minutes = (index % 4) * 15;
                          const time = `${hours
                            .toString()
                            .padStart(2, "0")}:${minutes
                            .toString()
                            .padStart(2, "0")}`;
                          return (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    {touched.endTime && errors.endTime && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.endTime}
                      </p>
                    )}
                  </div>
                </div>

                {/* Dates Section */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div className="space-y-2">
                    <label className="block text-foreground font-medium">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <Calendar className="w-5 h-5 text-foreground" />
                      <input
                        type="date"
                        min={
                          id
                            ? undefined
                            : new Date().toISOString().split("T")[0]
                        }
                        value={formData.startDate || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            startDate: e.target.value,
                          }));
                          if (touched.startDate) {
                            validateField("startDate");
                          }
                        }}
                        onBlur={() => handleBlur("startDate")}
                        className={`bg-transparent w-full focus:outline-none text-foreground ${
                          errors.startDate ? "border-b-2 border-red-500" : ""
                        }`}
                      />
                    </div>
                    {touched.startDate && errors.startDate && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.startDate}
                      </p>
                    )}
                  </div>

                  {/* End Date */}
                  <div className="space-y-2">
                    <label className="block text-foreground font-medium">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                      <Calendar className="w-5 h-5 text-foreground" />
                      <input
                        type="date"
                        min={
                          formData.startDate
                            ? new Date(
                                new Date(formData.startDate).getTime() +
                                  86400000,
                              )
                                .toISOString()
                                .split("T")[0]
                            : today
                        }
                        value={formData.endDate || ""}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            endDate: e.target.value,
                          }));
                          if (touched.endDate) {
                            validateField("endDate");
                          }
                        }}
                        onBlur={() => handleBlur("endDate")}
                        className={`bg-transparent w-full focus:outline-none text-foreground ${
                          !formData.startDate
                            ? "cursor-not-allowed opacity-50"
                            : ""
                        } ${errors.endDate ? "border-b-2 border-red-500" : ""}`}
                        disabled={!formData.startDate}
                        required
                      />
                    </div>
                    {touched.endDate && errors.endDate && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.endDate}
                      </p>
                    )}
                    {!formData.startDate && (
                      <p className="text-sm text-foreground">
                        Please select a start date first
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* White Label Options */}
            <hr />
            <div className="space-y-4">
              <div className="flex items-center -mt-4 gap-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isAnonymizedView}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isAnonymizedView: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-card after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-input after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-dark"></div>
                </label>
                <h2 className="text-xl font-semibold text-foreground">
                  {" "}
                  Anonymised View{" "}
                </h2>
              </div>
              <hr />
              <div className="flex items-center gap-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isWhiteLabel}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isWhiteLabel: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-card after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-input after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-dark"></div>
                </label>

                <h2 className="text-xl font-semibold text-foreground">
                  White Label Campaign
                </h2>
              </div>
              <hr />

              <div className="flex items-center gap-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_top}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        show_top: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-card after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-card after:border-input after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-dark"></div>
                </label>

                <h2 className="text-xl font-semibold text-foreground">
                  Display Vacancies at Top
                </h2>
              </div>
              <hr />

              {formData.isWhiteLabel && (
                <div className="space-y-4 bg-muted p-4 rounded-lg">
                  {/* Custom URL */}
                  <div className="space-y-2">
                    <label
                      htmlFor="customUrl"
                      className="block text-foreground font-medium"
                    >
                      Custom URL
                    </label>
                    <div className="flex items-center gap-2 bg-card rounded-lg p-3">
                      <Globe className="w-5 h-5 text-foreground" />
                      <input
                        type="text"
                        id="customUrl"
                        name="customUrl"
                        value={formData.customUrl}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            customUrl: e.target.value,
                          })
                        }
                        className="bg-transparent w-full focus:outline-none text-foreground"
                        placeholder="company.apptrkr.com"
                      />
                    </div>
                  </div>

                  {/* Custom Email */}
                  <div className="space-y-2">
                    <label
                      htmlFor="customEmail"
                      className="block text-foreground font-medium"
                    >
                      Custom Email Address
                    </label>
                    <div className="flex items-center gap-2 bg-card rounded-lg p-3">
                      <Mail className="w-5 h-5 text-foreground" />
                      <input
                        type="email"
                        id="customEmail"
                        name="customEmail"
                        value={formData.customEmail}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            customEmail: e.target.value,
                          })
                        }
                        className="bg-transparent w-full focus:outline-none text-foreground"
                        placeholder="careers@company.com"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Campaign Microsite */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground mb-4">
                Campaign Microsite
              </h2>
              <CampaignMicrosite
                micrositeData={micrositeData}
                setMicrositeData={setMicrositeData}
              />
            </div>

            {/* Page Elements Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">Page Elements</h2>

              {/* Pre-Campaign Text Accordion */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === "preCampaign" ? null : "preCampaign",
                    )
                  }
                  className="w-full flex items-center justify-between p-4 bg-muted hover:bg-secondary-soft dark:hover:bg-accent"
                >
                  <span className="text-foreground font-medium">
                    Pre-Campaign Text
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${
                      expandedSection === "preCampaign"
                        ? "transform rotate-180"
                        : ""
                    }`}
                  />
                </button>
                {expandedSection === "preCampaign" && (
                  <div className="p-4">
                    <textarea
                      value={formData.preTextTitle}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          preTextTitle: e.target.value,
                        }));
                      }}
                      className="w-full p-3 bg-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-foreground"
                      rows={4}
                      placeholder="Enter Pre text Title..."
                    />
                    <textarea
                      value={formData.preCampaignText}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          preCampaignText: e.target.value,
                        }));
                      }}
                      className="w-full p-3 bg-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-foreground"
                      rows={4}
                      placeholder="Enter text to display before the campaign starts..."
                    />
                  </div>
                )}
              </div>

              {/* Campaign End Message Accordion */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === "campaignEnd" ? null : "campaignEnd",
                    )
                  }
                  className="w-full flex items-center justify-between p-4 bg-muted hover:bg-secondary-soft dark:hover:bg-accent"
                >
                  <span className="text-foreground font-medium">
                    Campaign End Message
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${
                      expandedSection === "campaignEnd"
                        ? "transform rotate-180"
                        : ""
                    }`}
                  />
                </button>
                {expandedSection === "campaignEnd" && (
                  <div className="p-4">
                    <textarea
                      value={formData.postTextTitle}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          postTextTitle: e.target.value,
                        }));
                      }}
                      className="w-full p-3 bg-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-foreground"
                      rows={4}
                      placeholder="Enter Post text Title..."
                    />
                    <textarea
                      value={formData.campaignEndMessage}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          campaignEndMessage: e.target.value,
                        }));
                      }}
                      className="w-full p-3 bg-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-brand text-foreground"
                      rows={4}
                      placeholder="Enter message to display after the campaign ends..."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              {/* Save as Draft */}
              <button
                type="button"
                onClick={(e) => handleSubmit(e, "draft")}
                disabled={isSavingDraft || isPreviewing}
                className={[
                  "flex-1 bg-muted text-foreground hover:bg-secondary-soft dark:hover:bg-accent py-4 px-6 rounded-lg shadow-md transition-colors duration-200 flex items-center justify-center gap-2 font-semibold relative overflow-hidden",
                  isSavingDraft || isPreviewing
                    ? "opacity-50 cursor-not-allowed"
                    : "",
                ].join(" ")}
              >
                {isSavingDraft ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-foreground" />
                    <span className="animate-pulse">Saving Draft...</span>
                  </div>
                ) : (
                  <>Save as Draft</>
                )}
                {isSavingDraft && (
                  <div className="absolute inset-0 bg-muted opacity-50"></div>
                )}
              </button>

              {/* Preview Button */}
              <button
                type="button"
                onClick={() => setIsPreviewOpen(true)}
                className="flex-1 bg-muted text-foreground hover:bg-secondary-soft dark:hover:bg-accent py-4 px-6 rounded-lg shadow-md transition-colors duration-200 flex items-center justify-center gap-2 font-semibold relative overflow-hidden"
              >
                Preview
              </button>
              {/* Publish Button */}
              <button
                type="submit"
                disabled={isUpdating || isCreating}
                className="flex-1 bg-primary-dark hover:brightness-110 text-white py-4 px-6 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center gap-2 font-semibold relative overflow-hidden"
              >
                {isUpdating || isCreating ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                    <span className="animate-pulse">
                      {id
                        ? formData.status === "draft"
                          ? "Publishing..."
                          : "Updating..."
                        : "Publishing..."}
                    </span>
                  </div>
                ) : (
                  <>
                    {id
                      ? formData.status === "draft"
                        ? "Publish Campaign"
                        : "Update Campaign"
                      : "Publish Campaign"}
                  </>
                )}
                {(isUpdating || isCreating) && (
                  <div className="absolute inset-0 bg-primary opacity-50"></div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <CampaignPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        formData={formData}
        selectedCompany={selectedCompany}
        micrositeData={micrositeData}
      />
    </div>
  );
};

export default CampaignForm;
